import React, { useEffect, useState } from "react"
import classnames from "classnames"
import * as layouts from "layouts"
import * as errors from "errors"
import * as httpx from "httpx"
import * as authzc from "authzcached"
import * as api from "brandguard/api"
import * as bgscores from "brandguard/display/scores"
import { ScoreAccordionDescription, SubSubScoreContainer } from "brandguard/components/layouts/scores"
import * as icons from "icons"

interface props extends layouts.containers.FlexProps {
	event: api.Media
	contentType: "text" | "image" | "document" | "video"
}

export default function Display(props: React.PropsWithChildren<props>): JSX.Element {
	const { className, event, contentType, children, ...rest } = props

	const metaauthz = authzc.useCache((cached) => cached.meta)
	const [cause, setCause] = useState(undefined as undefined | JSX.Element)
	const [req, setReq] = useState(
		api.explanations.productDetection.searches.request({
			event_ids: [event.id],
		}),
	)
	const [explanations, setExplanations] = useState({
		next: req,
		items: [],
	} as api.ProductDetectionExplanationSearchResponse)
	const [loading, setloading] = useState(false)

	useEffect(() => {
		setloading(true)
		const retry = httpx.autoretry()

		let searchFn: typeof api.explanations.imageProductDetection.search
		switch (contentType) {
			case "image":
				searchFn = api.explanations.imageProductDetection.search
				break
			case "video":
				searchFn = api.explanations.videoProductDetection.search
				break
			case "document":
				searchFn = api.explanations.document.productdetection.search
				break
			case "text":
				searchFn = api.explanations.textProductDetection.search
				break
			default:
				searchFn = api.explanations.imageProductDetection.search
		}

		const pending = retry
			.wrap(() => searchFn(req, authzc.bearer(metaauthz)))
			.then(setExplanations)
			.catch(httpx.errors.cancellation(console.debug))
			.catch((cause) => {
				setCause(<errors.UnknownCause logging={console.error} cause={cause} onClick={() => setCause(undefined)} />)
			})
			.finally(() => setloading(false))

		return pending.cancel
	}, [req, contentType])

	useEffect(() => {
		setReq(
			api.explanations.productDetection.searches.request({
				event_ids: [event.id],
			}),
		)
	}, [event.id])

	if (loading)
		return (
			<SubSubScoreContainer className={className}>
				<layouts.containers.flex flexDirection="row" flex="1">
					<layouts.containers.flex flex="3" justifyContent="flex-start" alignItems="center" color={"unset"}>
						Product Detection
					</layouts.containers.flex>
					<layouts.containers.flex flex="1" justifyContent="flex-end" alignItems="center">
						<icons.loading.Ring3 width="14px" height="14px" />
					</layouts.containers.flex>
				</layouts.containers.flex>
			</SubSubScoreContainer>
		)
	if (cause) return <layouts.Flex p="10px">{cause}</layouts.Flex>
	if (explanations.items.length === 0) return <>{children}</>

	const allProducts = explanations.items.flatMap((e) => e.detected_products || [])
	if (allProducts.length === 0) return <>{children}</>

	return (
		<bgscores.SectionAccordion
			borderWidth="0"
			borderRadius="0"
			borderColor="unset"
			borderStyle="unset"
			borderBottom="none"
			my="0"
			className={classnames(className, bgscores.layouts.styledAccordion)}
			{...rest}
		>
			<layouts.containers.box tabIndex={0}>
				<bgscores.SectionDescription>
					<ScoreAccordionDescription padding="0" className={className}>
						{children}
					</ScoreAccordionDescription>
				</bgscores.SectionDescription>
				<layouts.accordions.Content>
					<layouts.Flex flexDirection="column" flex="1" pr="54px" className={classnames("products.list", className)}>
						<errors.overlay styled cause={cause} flex="1">
							<layouts.Flex flexWrap="wrap" gridGap="8px" pl="35px" py="10px">
								{allProducts.map((p, idx) => (
									<ProductTag key={`${p.product_id}-${idx}`} product={p} />
								))}
							</layouts.Flex>
						</errors.overlay>
					</layouts.Flex>
				</layouts.accordions.Content>
			</layouts.containers.box>
		</bgscores.SectionAccordion>
	)
}

interface productTagProps {
	product: api.DetectedProduct
}

function ProductTag(props: productTagProps): JSX.Element {
	const { product } = props
	return (
		<layouts.Flex
			px="12px"
			py="6px"
			borderRadius="16px"
			background={layouts.theme.colors.blue.blue}
			color={layouts.theme.colors.white}
			fontSize="12px"
			fontWeight="500"
			alignItems="center"
			title={product.aliases_matched?.length > 0 ? `Matched: ${product.aliases_matched.join(", ")}` : undefined}
		>
			{product.name}
			{product.confidence > 0 && product.confidence < 1 && (
				<layouts.Span ml="4px" opacity="0.7" fontSize="10px">
					({Math.round(product.confidence * 100)}%)
				</layouts.Span>
			)}
		</layouts.Flex>
	)
}
